import { create } from 'zustand';
import axios from 'axios';
import { LocalPinyinGenerator } from '../utils/pinyinGenerator';
import * as db from '../services/db';
import { LocalTranslation, TranslationInput } from '../services/db';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Request deduplication to prevent duplicate API calls
const pendingRequests = new Map<string, Promise<any>>();

const getRequestKey = (text: string, context?: string) =>
  `${text.toLowerCase().trim()}_${context || 'no-context'}`;

interface AlternativeTerm {
  term: string;
  pinyin?: string;
  context: string;
}

interface Translation {
  original: string;
  cleaned?: string;
  translation: string;
  officialTranslation: string;
  alternatives?: AlternativeTerm[];
  pinyin?: string;
  examples: string[];
  category?: string;
}

interface HistoryItem extends Translation {
  id: string;
  sourceLang: string;
  targetLang: string;
  searchCount: number;
  lastSearched: Date;
}

interface TranslationStore {
  currentTranslation: Translation | null;
  history: HistoryItem[];
  categorizedHistory: Record<string, HistoryItem[]>;
  loading: boolean;
  error: string | null;
  sourceLang: 'en' | 'zh';
  targetLang: 'en' | 'zh';
  searchText: string;
  dbInitialized: boolean;
  // TTS state
  isSpeaking: boolean;
  speakingText: string | null;
  ttsAvailable: boolean;
  // Performance metrics
  lastTranslationMetrics: {
    apiTime: number;
    localPinyinTime: number;
    totalTime: number;
  } | null;

  // Core actions
  initializeDB: () => Promise<void>;
  setSourceLang: (lang: 'en' | 'zh') => void;
  setTargetLang: (lang: 'en' | 'zh') => void;
  swapLanguages: () => void;
  translate: (text: string, context?: string) => Promise<void>;
  loadHistoryItem: (historyItem: HistoryItem) => Promise<void>;
  setSearchText: (text: string) => void;
  fetchHistory: () => Promise<void>;
  fetchCategorizedHistory: () => Promise<void>;
  deleteHistoryItem: (id: string) => Promise<void>;
  recategorizeHistory: () => Promise<void>;
  clearError: () => void;
  clearHistory: () => Promise<void>;
  // Data management actions
  exportData: () => Promise<void>;
  importData: (file: File) => Promise<{ imported: number; merged: number; skipped: number }>;
  // TTS actions
  setTTSState: (isSpeaking: boolean, text: string | null) => void;
  setTTSAvailable: (available: boolean) => void;
  stopAllSpeech: () => void;
}

// Helper to convert LocalTranslation to HistoryItem
const toHistoryItem = (t: LocalTranslation): HistoryItem => ({
  id: t.id,
  original: t.original,
  translation: t.translation,
  officialTranslation: t.officialTranslation,
  alternatives: t.alternatives,
  pinyin: t.pinyin,
  examples: t.examples,
  category: t.category,
  sourceLang: t.sourceLang,
  targetLang: t.targetLang,
  searchCount: t.searchCount,
  lastSearched: t.lastSearched
});

export const useTranslationStore = create<TranslationStore>((set, get) => {
  // Preload common Chinese words for instant pinyin generation
  try {
    LocalPinyinGenerator.preloadCommonWords();
  } catch (error) {
    console.warn('Failed to preload pinyin cache:', error);
  }

  return {
    currentTranslation: null,
    history: [],
    categorizedHistory: {},
    loading: false,
    error: null,
    sourceLang: 'en',
    targetLang: 'zh',
    searchText: '',
    dbInitialized: false,
    // TTS state
    isSpeaking: false,
    speakingText: null,
    ttsAvailable: false,
    lastTranslationMetrics: null,

    initializeDB: async () => {
      try {
        await db.initializeDB();
        set({ dbInitialized: true });
        // Load history from IndexedDB
        await get().fetchHistory();
        await get().fetchCategorizedHistory();
      } catch (error) {
        console.error('Failed to initialize database:', error);
        set({ error: 'Failed to initialize local database' });
      }
    },

    setSourceLang: (lang) => {
      // Only allow English as source
      if (lang === 'en') {
        set({ sourceLang: lang, targetLang: 'zh' });
      }
    },

    setTargetLang: (lang) => {
      // Only allow Chinese as target
      if (lang === 'zh') {
        set({ sourceLang: 'en', targetLang: lang });
      }
    },

    swapLanguages: () => {
      // Disabled - only English to Chinese allowed
      return;
    },

    translate: async (text: string, context?: string) => {
      const totalStartTime = performance.now();
      const { sourceLang, targetLang } = get();
      const requestKey = getRequestKey(text, context);

      // Check if there's already a pending request for this text
      if (pendingRequests.has(requestKey)) {
        console.log(`Deduplicating request for: ${text}`);
        await pendingRequests.get(requestKey);
        return;
      }

      // Reset states
      set({
        loading: true,
        error: null,
        lastTranslationMetrics: null
      });

      console.log(`Starting translation for: ${text}`);

      const requestPromise = (async () => {
        try {
          const apiStartTime = performance.now();

          // Call translation API (no auth headers needed)
          const response = await axios.post(`${API_URL}/translate`, {
            text,
            context,
            sourceLang,
            targetLang,
          });

          const apiEndTime = performance.now();
          const apiTime = apiEndTime - apiStartTime;

          // Generate local pinyin for Chinese translation
          const localPinyinStartTime = performance.now();
          const localPinyin = targetLang === 'zh' && response.data.translation
            ? LocalPinyinGenerator.generate(response.data.translation)
            : undefined;
          const localPinyinEndTime = performance.now();
          const localPinyinTime = localPinyinEndTime - localPinyinStartTime;

          // Generate local pinyin for alternatives
          const alternativesWithPinyin = (response.data.alternatives || []).map((alt: any) => ({
            ...alt,
            pinyin: alt.term ? LocalPinyinGenerator.generate(alt.term) : undefined
          }));

          // Generate local pinyin for examples
          const examplesWithPinyin = (response.data.examples || []).map((example: string) => {
            const parts = example.split(' - ');
            if (parts.length === 2) {
              const chinesePart = parts[0].trim();
              const englishPart = parts[1].trim();
              const localExamplePinyin = LocalPinyinGenerator.generate(chinesePart);
              return `${englishPart} - ${chinesePart} - ${localExamplePinyin}`;
            }
            return example;
          });

          const fullTranslation: Translation = {
            original: response.data.original,
            cleaned: response.data.cleaned,
            translation: response.data.translation,
            officialTranslation: response.data.officialTranslation,
            pinyin: localPinyin,
            alternatives: alternativesWithPinyin,
            examples: examplesWithPinyin,
            category: response.data.category
          };

          const totalTime = performance.now() - totalStartTime;
          console.log(`Translation completed in ${totalTime.toFixed(2)}ms`);

          set({
            currentTranslation: fullTranslation,
            searchText: '',
            loading: false,
            lastTranslationMetrics: {
              apiTime,
              localPinyinTime,
              totalTime
            }
          });

          // Save to local IndexedDB
          try {
            const translationInput: TranslationInput = {
              original: response.data.original,
              cleaned: response.data.cleaned,
              translation: response.data.translation,
              officialTranslation: response.data.officialTranslation,
              alternatives: alternativesWithPinyin,
              pinyin: localPinyin || '',
              examples: examplesWithPinyin,
              category: response.data.category || 'General',
              context: context || null,
              sourceLang,
              targetLang,
            };

            await db.saveOrMergeTranslation(translationInput);

            // Refresh history from IndexedDB
            await get().fetchHistory();
            await get().fetchCategorizedHistory();
          } catch (dbError) {
            console.warn('Failed to save to local storage:', dbError);
          }

        } catch (error: any) {
          const totalTime = performance.now() - totalStartTime;
          const metrics = {
            apiTime: 0,
            localPinyinTime: 0,
            totalTime
          };

          set({
            error: error.response?.data?.error || error.message || 'Translation failed',
            loading: false,
            lastTranslationMetrics: metrics
          });

          console.error('Translation failed:', error);
        } finally {
          pendingRequests.delete(requestKey);
        }
      })();

      pendingRequests.set(requestKey, requestPromise);
      await requestPromise;
    },

    loadHistoryItem: async (historyItem: HistoryItem) => {
      try {
        // Increment search count in IndexedDB
        const updated = await db.incrementSearchCount(historyItem.id);

        const translation: Translation = {
          original: historyItem.original,
          translation: historyItem.translation,
          officialTranslation: historyItem.officialTranslation,
          alternatives: historyItem.alternatives,
          pinyin: historyItem.pinyin,
          examples: historyItem.examples,
          category: historyItem.category,
        };

        set({
          currentTranslation: translation,
          error: null,
          searchText: historyItem.original
        });

        // Update local history with new count
        if (updated) {
          const { history } = get();
          const updatedHistory = history.map(item =>
            item.id === historyItem.id
              ? { ...item, searchCount: updated.searchCount, lastSearched: updated.lastSearched }
              : item
          );
          set({ history: updatedHistory });
        }

      } catch (error) {
        console.error('Failed to load history item:', error);

        // Fallback to just loading the item
        const translation: Translation = {
          original: historyItem.original,
          translation: historyItem.translation,
          officialTranslation: historyItem.officialTranslation,
          alternatives: historyItem.alternatives,
          pinyin: historyItem.pinyin,
          examples: historyItem.examples,
          category: historyItem.category,
        };

        set({
          currentTranslation: translation,
          error: null,
          searchText: historyItem.original
        });
      }
    },

    setSearchText: (text: string) => {
      set({ searchText: text });
    },

    fetchHistory: async () => {
      try {
        const translations = await db.getAllTranslations();
        const historyItems = translations.map(toHistoryItem);
        set({ history: historyItems });
      } catch (error) {
        console.error('Failed to fetch history:', error);
        set({ history: [] });
      }
    },

    fetchCategorizedHistory: async () => {
      try {
        const categorized = await db.getCategorizedHistory();
        const result: Record<string, HistoryItem[]> = {};

        for (const [category, translations] of Object.entries(categorized)) {
          result[category] = translations.map(toHistoryItem);
        }

        set({ categorizedHistory: result });
      } catch (error) {
        console.error('Failed to fetch categorized history:', error);
        set({ categorizedHistory: {} });
      }
    },

    deleteHistoryItem: async (id: string) => {
      try {
        await db.deleteTranslation(id);

        // Update local state
        const { history, categorizedHistory } = get();
        const updatedHistory = history.filter(item => item.id !== id);

        const updatedCategorizedHistory = { ...categorizedHistory };
        Object.keys(updatedCategorizedHistory).forEach(category => {
          updatedCategorizedHistory[category] = updatedCategorizedHistory[category].filter(item => item.id !== id);
          if (updatedCategorizedHistory[category].length === 0) {
            delete updatedCategorizedHistory[category];
          }
        });

        set({
          history: updatedHistory,
          categorizedHistory: updatedCategorizedHistory,
          error: null
        });
      } catch (error) {
        console.error('Failed to delete history item:', error);
        set({ error: 'Failed to delete history item' });
      }
    },

    recategorizeHistory: async () => {
      try {
        set({ loading: true });

        // Get all translations from IndexedDB
        const translations = await db.getAllTranslations();

        if (translations.length === 0) {
          set({ loading: false });
          return;
        }

        // Prepare data for categorization API
        const words = translations.map(t => ({
          chinese: t.translation,
          english: t.original
        }));

        // Call backend categorization API
        const response = await axios.post(`${API_URL}/translate/categorize`, { words });
        const newCategories: Record<string, string> = response.data.categories || response.data;

        // Update all categories in IndexedDB
        await db.updateAllCategories(newCategories);

        // Refresh history views
        await get().fetchHistory();
        await get().fetchCategorizedHistory();

        set({ loading: false });
      } catch (error) {
        console.error('Failed to recategorize history:', error);
        set({ error: 'Failed to recategorize history', loading: false });
      }
    },

    clearHistory: async () => {
      try {
        await db.clearAllData();
        set({ history: [], categorizedHistory: {} });
      } catch (error) {
        console.error('Failed to clear history:', error);
        set({ error: 'Failed to clear history' });
      }
    },

    clearError: () => set({ error: null }),

    // Data management actions
    exportData: async () => {
      try {
        const json = await db.exportToJSON();
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const date = new Date().toISOString().split('T')[0];
        const filename = `translatecard-backup-${date}.json`;

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to export data:', error);
        set({ error: 'Failed to export data' });
      }
    },

    importData: async (file: File) => {
      try {
        const text = await file.text();
        const result = await db.importFromJSON(text);

        // Refresh history views
        await get().fetchHistory();
        await get().fetchCategorizedHistory();

        return result;
      } catch (error) {
        console.error('Failed to import data:', error);
        set({ error: 'Failed to import data' });
        throw error;
      }
    },

    // TTS actions
    setTTSState: (isSpeaking: boolean, text: string | null) => {
      set({ isSpeaking, speakingText: text });
    },

    setTTSAvailable: (available: boolean) => {
      set({ ttsAvailable: available });
    },

    stopAllSpeech: () => {
      set({ isSpeaking: false, speakingText: null });
    },
  };
});
